/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/components/Callout';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { AzureCsAccountStore, AzureSku } from 'src/core/stores/AzureCsAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { isTextAnalyticsResource } from 'src/core/utils/csAccountUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    CluMigrationContentSideMargin,
    CluMigrationInfoDto,
    CluMigrationSteps
} from 'src/luis/verticals/cluMigrationModal/CluMigrationModal';
import { CluMigrationModalFooter } from 'src/luis/verticals/cluMigrationModal/components/CluMigrationModalFooter';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';

export type CluMigrationModalSelectLanguageResourceProps = {
    onDismiss: () => void;
    onPrevious: () => void;
    cluMigrationInfoDto: CluMigrationInfoDto;
    onSubmit: (languageResource: AzureCsAccountStore) => void;
};

const messages = defineMessages({
    title: {
        id: 'cluMigration.selectLanguageResourceScreen.title',
        defaultMessage: 'Select Language resource'
    },
    description1: {
        id: 'cluMigration.selectLanguageResourceScreen.description1',
        defaultMessage: 'Conversational language understanding exists as part of the'
    },
    languageService: {
        id: 'cluMigration.selectLanguageResourceScreen.languageService',
        defaultMessage: 'Language service.'
    },
    description2: {
        id: 'cluMigration.selectLanguageResourceScreen.description2',
        defaultMessage: 'You must select the Azure subscription and'
    },
    languageResource: {
        id: 'cluMigration.selectLanguageResourceScreen.languageResource',
        defaultMessage: 'Language resource'
    },
    description3: {
        id: 'cluMigration.selectLanguageResourceScreen.description3',
        defaultMessage:
            'to copy your applications to. If you do not have a Language resource, you can create a new one. You can only migrate to the following regions listed'
    },
    azureSubscription: {
        id: 'cluMigration.selectLanguageResourceScreen.azureSubscription',
        defaultMessage: 'Azure subscription'
    },
    azureSubscriptionPlaceholder: {
        id: 'cluMigration.selectLanguageResourceScreen.azureSubscriptionPlaceholder',
        defaultMessage: 'Select Azure subscription'
    },
    languageResourcePlaceholder: {
        id: 'cluMigration.selectLanguageResourceScreen.languageResourcePlaceholder',
        defaultMessage: 'Select Language resource'
    },
    createNewLanguageResource: {
        id: 'cluMigration.selectLanguageResourceScreen.createNewLanguageResource',
        defaultMessage: 'Create a new Language resource'
    },
    here: {
        id: 'cluMigration.selectLanguageResourceScreen.here',
        defaultMessage: 'here.'
    }
});

const modalTheme = getScopedTheme('CluMigrationModalBase');
const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { alignSelf: 'flex-start', color: modalTheme.buttonColor }
});
const dropdownStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});

export const SelectLanguageResourceScreen = observer((props: CluMigrationModalSelectLanguageResourceProps) => {
    const { cluMigrationInfoDto, onPrevious, onSubmit, onDismiss } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(userRole);
    const serviceTarget = 'TextAnalytics';

    const [subscriptionsErrorMessage, setSubscriptionsErrorMessage] = React.useState('');
    const subscriptionsValidators = [(id: string) => FormValidators.createRequiredValidator()(id)?.messageDescriptor];

    const [resourcesErrorMessage, setResourcesErrorMessage] = React.useState('');
    const resourcesValidators = [(id: string) => FormValidators.createRequiredValidator()(id)?.messageDescriptor];

    const [isCreateCsAccountModalHidden, setIsCreateCsAccountModalHidden] = React.useState(true);
    const defaultSelectedIds = {
        selectedResourceId: '',
        selectedResourceGroupId: '',
        selectedRegion: null as Region,
        selectedSkuCode: null as AzureSku['code'],
        selectedTenantId: rootStore.azureStore.session.tenantId,
        selectedSubscriptionId: cluMigrationInfoDto.languageResource?.subscriptionId ?? rootStore.azureStore.session.subscriptionId
    };

    const [azureResourceDto, updateAzureResourceDto] = useUpdateState(defaultSelectedIds);
    const [isFirstLoading, setIsFirstLoading] = React.useState(true);

    const { subscriptions, resourcesTracker, resources, subscriptionTracker } = useAzureResourcesManagement({
        subscriptionId: azureResourceDto.selectedSubscriptionId,
        skuStrategy: sku => sku.kind === serviceTarget,
        regionStrategy: region => region.kind === serviceTarget,
        resourceStrategy: resource =>
            isTextAnalyticsResource(resource) && LuisConstants.Clu.PrivatePreviewAllowedRegions.includes(resource.region),
        configs: { resources: true, skuAndRegions: !isCreateCsAccountModalHidden, resourceGroups: !isCreateCsAccountModalHidden }
    });

    const resourcesOptions = resources.map(item => ({
        key: item.id,
        text: item.name,
        data: { secondaryText: `(${item.region}, ${item.sku.code})` }
    }));

    React.useEffect(() => {
        if (isFirstLoading && resourcesTracker.isCompleted) {
            updateAzureResourceDto({ selectedResourceId: cluMigrationInfoDto.languageResource?.id ?? '' });
            setIsFirstLoading(false);
        }
    }, [resourcesTracker.state]);

    const onSubscriptionChange = (newSubscriptionId: string) => {
        const errors: ReactIntl.FormattedMessage.MessageDescriptor[] = subscriptionsValidators
            .reduce((acc, v) => [...acc, v(newSubscriptionId)], [])
            .filter(Boolean);
        const hasError = Boolean(errors.length);

        if (hasError) {
            setSubscriptionsErrorMessage(localize(errors[0]));
        } else if (subscriptionsErrorMessage) {
            setSubscriptionsErrorMessage('');
        }

        updateAzureResourceDto({ selectedSubscriptionId: newSubscriptionId, selectedResourceId: '' });
    };

    const onResourceChange = (newResourceId: string) => {
        const errors: ReactIntl.FormattedMessage.MessageDescriptor[] = resourcesValidators
            .reduce((acc, v) => [...acc, v(newResourceId)], [])
            .filter(Boolean);
        const hasError = Boolean(errors.length);

        if (hasError) {
            setResourcesErrorMessage(localize(errors[0]));
        } else if (subscriptionsErrorMessage) {
            setResourcesErrorMessage('');
        }

        updateAzureResourceDto({ selectedResourceId: newResourceId });
    };

    const renderDescription = () => (
        <LuisText>
            <LuisText>{localize(messages.description1)}</LuisText>
            &nbsp;
            <LuisText fontWeight={600}>{localize(messages.languageService)}</LuisText>
            &nbsp;
            <LuisText>{localize(messages.description2)}</LuisText>
            &nbsp;
            <LuisText fontWeight={600}>{localize(messages.languageResource)}</LuisText>
            &nbsp;
            <LuisText>{localize(messages.description3)}</LuisText>
            &nbsp;
            <ExternalLink href="https://aka.ms/clu-regions">{localize(messages.here)}</ExternalLink>
        </LuisText>
    );

    return (
        <Stack>
            <Stack
                styles={{ root: { minHeight: '500px', height: '100%', position: 'relative', overflowY: 'auto' } }}
                tokens={{ childrenGap: 15, padding: `0 0 5px ${CluMigrationContentSideMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    {renderDescription()}
                </Stack>

                <LuisDropdown
                    required
                    isLoading={subscriptionTracker.isRunning}
                    selectedKey={azureResourceDto.selectedSubscriptionId}
                    styles={{ root: { width: '300px' } }}
                    errorMessage={subscriptionsErrorMessage}
                    calloutProps={{ styles: dropdownStyles }}
                    data-automation-id="subscriptionsDropdown"
                    onChange={(_, opt) => onSubscriptionChange(opt.key as string)}
                    label={localize(messages.azureSubscription)}
                    options={subscriptions.map(item => ({ key: item.id, text: item.name }))}
                    placeholder={localize(messages.azureSubscriptionPlaceholder)}
                />
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisDropdown
                        required
                        label={localize(messages.languageResource)}
                        styles={{ root: { width: '300px' } }}
                        options={resourcesOptions}
                        ariaLabel={localize(messages.languageResource)}
                        isLoading={resourcesTracker.isRunning}
                        selectedKey={azureResourceDto.selectedResourceId}
                        placeholder={localize(messages.languageResourcePlaceholder)}
                        aria-labelledby={azureResourceDto.selectedResourceId}
                        errorMessage={resourcesErrorMessage}
                        data-automation-id="csAccountsDropdown"
                        calloutProps={{ styles: dropdownStyles }}
                        onChange={(_, item) => onResourceChange(item.key as string)}
                        disabled={Boolean(subscriptionsErrorMessage) || !Boolean(azureResourceDto.selectedSubscriptionId)}
                    />

                    <RBACTooltip
                        displayedUserRole={displayedUserRole}
                        hidden={canChangeAppSettingsAndDetails}
                        data-automation-id="createNewAccountButtonTooltip"
                        componentSurroundedByToolTip={
                            <ActionButton
                                styles={buttonStyles}
                                onClick={() => setIsCreateCsAccountModalHidden(false)}
                                data-automation-id="createAccountButton"
                                disabled={!canChangeAppSettingsAndDetails}>
                                {localize(messages.createNewLanguageResource)}
                            </ActionButton>
                        }
                    />
                </Stack>
            </Stack>

            <CluMigrationModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                isValid={Boolean(azureResourceDto.selectedResourceId)}
                onNext={() => onSubmit(resources.find(resource => resource.id === azureResourceDto.selectedResourceId))}
                activeStep={CluMigrationSteps.SelectLanguageResource}
            />

            <AzureResourcesManager
                serviceTarget="TextAnalytics"
                isCreateResourceVisible={!isCreateCsAccountModalHidden}
                selectedSubscriptionId={azureResourceDto.selectedSubscriptionId}
                onChangeCreateResourceVisible={isVisible => setIsCreateCsAccountModalHidden(!isVisible)}
                onChangeSubscription={subscriptionId =>
                    updateAzureResourceDto({ selectedSubscriptionId: subscriptionId, selectedResourceId: '' })
                }
            />
        </Stack>
    );
});
